/* SIZESOFTX
=========================================================================================
* Descripción: Modal de abonos a préstamos de empleados desde Venta Super.
*              Registra el abono en positivo con artículo "ABONO A PRESTAMOS {usuario}",
*              usa el folio normal de ventas como documento e incrementa el folio al terminar
*              para no perder consecutivos (no genera C_Remision ni mueve inventario).
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 14/10/2026 | Muse Spark                       | Creación
*      2       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Apertura automática de cajón para abono en efectivo
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { useEffect, useState } from "react";
import { Grid, Stack } from "@mui/material";
import SizeSoftXModal from "../leyout/SizeSoftXModal";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { apiNominasUsuarios, apiPrestamosRegistrarAbono, apiIncrementaVentasFolio } from "../../settings/ApiConfig";

const initialForm = { empleado: null, monto: "", observaciones: "" };

export default function ModalAbonoPrestamoVenta({ open, onClose, onSaved }) {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { usuarioData, parametrosCajaForm, handleObtenerParametrosCaja } = useSesion();
    const { handleAbrirCajonDinero } = useVentas();
    const [empleados, setEmpleados] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [guardando, setGuardando] = useState(false);

    const folioDocumento = parametrosCajaForm?.folioventa ?? 0;
    const caja = parametrosCajaForm?.caja || "";

    useEffect(() => {
        if (!open) return;
        setForm(initialForm);
        const cargarEmpleados = async () => {
            const response = await GetRequest({ url: apiNominasUsuarios });
            if (!response.hasError) setEmpleados(response.result || []);
        };
        cargarEmpleados();
    }, [open]);

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const articuloAbono = `ABONO A PRESTAMOS ${(form.empleado?.usuario || "").trim().toUpperCase()}`;

    const imprimirTicket = async (folio) => {
        const printerName = parametrosCajaForm?.nombreimpresoraventas?.trim() || localStorage.getItem("nombreimpresoraventas") || "";
        if (!printerName) return;
        await handleAbrirCajonDinero(printerName);
        const lineas = [
            (parametrosCajaForm?.encabezadoticket || "SIZESOFTX").trim(),
            "--------------------------------",
            "ABONO A PRESTAMO EMPLEADO",
            `REM: ${folio}  CAJA: ${caja}`,
            `Fecha: ${new Date().toLocaleString("es-MX")}`,
            `Articulo: ${articuloAbono}`,
            `Empleado: ${form.empleado?.nombre || ""}`,
            `Usuario: ${form.empleado?.usuario || ""}`,
            "--------------------------------",
            `Monto: $${Number(form.monto || 0).toFixed(2)}`,
            "Tipo pago: EFECTIVO",
            `Registra: ${usuarioData?.Usuario || ""}`,
            "--------------------------------",
            "Firma: _______________________",
            parametrosCajaForm?.pieticket || "",
        ].filter(Boolean);
        const bytes = new TextEncoder().encode("\x1B\x40" + lineas.join("\n") + "\n\n\n\n\x1D\x56\x00");
        const formData = new FormData();
        formData.append("ticket", new Blob([bytes], { type: "application/octet-stream" }));
        formData.append("printerName", printerName);
        await fetch("http://localhost:4000/api/imprimirticket", { method: "POST", body: formData });
    };

    const guardar = async () => {
        if (!form.empleado?.id && !form.empleado?.usuario) { handleOpenAlert("Seleccione un empleado", "warning"); return; }
        const monto = Number(String(form.monto || "").replace(/,/g, ""));
        if (!Number.isFinite(monto) || monto <= 0) { handleOpenAlert("Capture un monto en positivo mayor a cero", "warning"); return; }
        if (!caja || caja === "CAJA-DEFAULT") { handleOpenAlert("Caja no configurada", "warning"); return; }
        setGuardando(true);
        try {
            const body = {
                idaccesoempleado: Number(form.empleado.id || form.empleado.idaccesoempleado || 0),
                usuarioempleado: form.empleado.usuario || "",
                montoabono: monto,
                tipoPago: "EFECTIVO",
                tipopago: "EFECTIVO",
                foliotipopago: "",
                banco: "",
                observaciones: `${articuloAbono} REM-${folioDocumento}` + (form.observaciones ? ` | ${form.observaciones}` : ""),
                origenabono: "VENTA",
                caja: caja,
                usuarioregistro: usuarioData?.Usuario || "",
            };
            const response = await PostRequest({ url: apiPrestamosRegistrarAbono, body, loader: "Registrando abono..." });
            if (response.hasError) { handleOpenAlert(response.message, "error"); return; }
            await imprimirTicket(folioDocumento).catch(() => {});
            const incremento = await GetRequest({
                url: apiIncrementaVentasFolio.replace("{caja}", caja),
                loader: "Incrementando folio ventas por caja...",
            });
            if (incremento.hasError) {
                handleOpenAlert("Abono registrado, pero no se incrementó el folio: " + incremento.message, "warning");
            }
            await handleObtenerParametrosCaja();
            handleOpenAlert(`Abono registrado correctamente con REM-${folioDocumento}`, "success");
            onSaved?.(response.result);
            onClose?.();
        } catch (error) {
            handleOpenAlert("Error al registrar abono: " + error.message, "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <SizeSoftXModal open={open} title={`Abono préstamo empleado (REM-${folioDocumento})`} maxWidth="sm" success="Registrar" onSuccess={guardar} onClose={onClose} disableSuccess={guardando}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Autocomplete options={empleados} value={form.empleado} onChange={(_, value) => setField("empleado", value)} getOptionLabel={(option) => `${option?.nombre || ""} (${option?.usuario || ""})`} renderInput={(params) => <TextField autoComplete="off" {...params} label="Empleado" size="small" />} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}><TextField autoComplete="off" fullWidth size="small" label="Monto abono (positivo)" type="text" inputMode="decimal" value={form.monto} onChange={(e) => setField("monto", e.target.value)} /></Grid>
                    <Grid item xs={12} md={6}><TextField autoComplete="off" fullWidth size="small" label="Artículo" value={articuloAbono} InputProps={{ readOnly: true }} /></Grid>
                </Grid>
                <TextField autoComplete="off" fullWidth size="small" multiline minRows={2} label="Observaciones" value={form.observaciones} onChange={(e) => setField("observaciones", e.target.value)} />
            </Stack>
        </SizeSoftXModal>
    );
}
